// Admin → Scene Scrub: the ONE phrase list (A-3: D-A reaction phrases + S-7 stage
// directions) and the cut-list settings (quiet period, pads, reaction cap, path prefix,
// rough-cut join offset / voice cross / reaction join / completeness; media fallback channels + voice channel;
// Rough Cut v2: placement mode, take-map model + spend cap, pinned sequence format, whole-take cap, ALL TAKES toggle;
// Rough Cut v3: join style — straight (default) or jl).
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const KINDS = [['both', 'Reaction + direction'], ['expression', 'Reaction phrase (D-A)'], ['direction', 'Stage direction (S-7)']];
// Q-2 (D-179): the budget keys (take-map / vision caps, screening reserve, the run hard cap) left this list — Admin → Scrub Queue only
const SETTINGS = [
  ['quiet_minutes', 'Auto-build quiet period (minutes)', 'number'],
  ['pad_head_sec', 'Head pad before first word (s)', 'number'],
  ['pad_tail_sec', 'Tail pad after last word (s)', 'number'],
  ['reaction_cap_sec', 'Reaction take cap from action (s)', 'number'],
  ['timebase', 'XML timebase (frames)', 'number'],
  // Rough cut (Context.HeliconflowRoughCut.md R-4 / R-5 / R-7)
  ['join_offset_frames', 'Rough cut: picture-to-sound offset at a join (frames)', 'number'],
  ['voice_cross_frames', 'Rough cut: the two voices cross by (frames)', 'number'],
  ['reaction_join_frames', 'Rough cut: offset into/out of a reaction shot (frames)', 'number'],
  ['complete_coverage', 'Rough cut: a take is complete at this share of the scripted words (0–1)', 'number'],
  // Media properties (Context.HeliconflowMediaProps.md Phase 4)
  ['audio_channels', 'Audio channels assumed for a file whose probe failed (files are declared with their real count when known)', 'number'],
  ['voice_source_channel', 'Source audio channel carrying the voice (FX3 shoots: 1)', 'number'],
  ['path_prefix_default', 'Clip path prefix template (blank = bare file names; use {file})', 'text'],
  // Rough Cut v2 (Context.HeliconflowRoughCutV2r2.md W-1 / W-5 / W-6 / W-7 / W-9)
  ['placement_mode', 'Placement: how takes are found (takemap = the model reads every clip; state = the on-set cue state machine; legacy = the older dialogue-first matcher)', 'select', ['takemap', 'state', 'legacy']],
  ['takemap_model', 'Take map: model that reads the clips (a model id, e.g. claude-opus-5 — changing it needs no code)', 'text'],
  ['sequence_width', 'Sequence format: width (px) — every sequence in the download is pinned to this', 'number'],
  ['sequence_height', 'Sequence format: height (px) — defaults 1080 × 1920 (vertical)', 'number'],
  ['whole_take_cap_sec', 'Rough cut: a whole-take fallback longer than this becomes a labelled hole (seconds)', 'number'],
  // Rough Cut v3 (Context.HeliconflowRoughCutV3.md X-1)
  ['join_style', 'Rough cut joins: straight = every join is a straight cut (default); jl = J/L sound offsets chosen by listening to the handles', 'select', ['straight', 'jl']],
  ['include_all_takes', 'Download: also include the 02 ALL TAKES sequence (off = the rough cut only; alternates live in the panel)', 'boolean'],
  // Visual index — the eye (Context.HeliconflowVisualIndex.md V-6)
  ['visual_index_enabled', 'Visual index (the eye): sample frames from every clip and read them with a vision model (off = zero vision calls)', 'boolean'],
  ['visual_model', 'Visual index: vision model that reads the frames (a model id, e.g. claude-haiku-4-5)', 'text'],
  ['visual_sample_sec', 'Visual index: one frame every N seconds (0.5–60)', 'number'],
  // Rough Cut v3b (Context.HeliconflowRoughCutV3b.md Y-1, D-138)
  ['span_check_mode', "The eye's span check: corroborate = a take nobody is mouthing becomes a director feed only when its character is not the clip's bound on-camera character (default); strict = any such take (v3); off = never", 'select', ['corroborate', 'strict', 'off']],
  // Rough Cut v4 (Context.HeliconflowRoughCutV4.md Z-1) — the judge
  ['judge_mode', 'The judge: strict = the words check is the gate (default); judge = one model pass reads every real delivery in the story and the room and may place a VARIANT (changed line the set accepted) — verbatim words and reason shown', 'select', ['strict', 'judge']],
];

// D-183 F-2: the three engine knobs scoped per script format (no new knobs; the witness density is pinned by D-178, not a setting)
const FORMATS = ['Skit', 'Mockumentary', 'TH', 'Longform'];
const FORMAT_KNOBS = [['pad_tail_sec', 'Tail pad (s)', null], ['join_style', 'Joins', ['straight', 'jl']], ['vo_pass_face', 'VO pass face', ['listener', 'speaker']]];

export default function SceneScrubAdmin() {
  const [phrases, setPhrases] = useState([]);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ phrase: '', kind: 'both' });
  const [edits, setEdits] = useState({});
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get('/api/scene-scrub/phrases'), api.get('/api/scene-scrub/settings')]);
      setPhrases(p.data); setSettings(s.data); setEdits({});
    } catch (e) { setErr(e.response?.data?.error || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function addPhrase(e) {
    e.preventDefault(); setErr(''); setMsg('');
    if (!draft.phrase.trim()) return;
    try { await api.post('/api/scene-scrub/phrases', draft); setDraft({ phrase: '', kind: 'both' }); setMsg('Phrase added.'); load(); }
    catch (e2) { setErr(e2.response?.data?.error || 'Add failed'); }
  }
  async function removePhrase(p) {
    setErr(''); setMsg('');
    try { await api.delete(`/api/scene-scrub/phrases/${p.id}`); setMsg(`Removed "${p.phrase}".`); load(); }
    catch (e2) { setErr(e2.response?.data?.error || 'Delete failed'); }
  }
  async function saveSettings() {
    setErr(''); setMsg('');
    const body = {};
    for (const [k, , type] of SETTINGS) if (k in edits) body[k] = type === 'number' ? Number(edits[k]) : type === 'boolean' ? edits[k] === true || edits[k] === 'true' : edits[k];
    // D-183 F-2: per-format rows — '' = back to the global value (the row is removed)
    for (const [k, v] of Object.entries(edits)) if (k.startsWith('format.')) body[k] = v === '' ? null : (k.endsWith('.pad_tail_sec') ? Number(v) : v);
    if (!Object.keys(body).length) return;
    try { const r = await api.put('/api/scene-scrub/settings', body); setSettings(r.data); setEdits({}); setMsg('Settings saved.'); }
    catch (e2) { setErr(e2.response?.data?.error || 'Save failed'); }
  }

  return (
    <div style={{ display: 'grid', gap: 24, maxWidth: 900 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Phrase list</div>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 10 }}>
          One list serves two rules. A script row is a <b>reaction scene</b> when its Line cell is empty, or is ≤6 words and contains a reaction phrase. A row is a <b>stage direction</b> (non-speaking) when its Line is ≤4 words, has no sentence punctuation and matches a direction phrase. A row with real dialogue is never non-speaking, whatever this list says. VO rows are always speaking.
        </div>
        <form onSubmit={addPhrase} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input className="vc-input" data-testid="scene-scrub-phrase-input" placeholder="new phrase (e.g. shakes head)" value={draft.phrase} onChange={e => setDraft(d => ({ ...d, phrase: e.target.value }))} style={{ flex: 1 }} />
          <select className="vc-input" value={draft.kind} onChange={e => setDraft(d => ({ ...d, kind: e.target.value }))} style={{ width: 220 }}>
            {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button className="btn-primary" type="submit">Add</button>
        </form>
        <table className="adm-table" data-testid="scene-scrub-phrases">
          <thead><tr><th className="adm-th">Phrase</th><th className="adm-th">Applies to</th><th className="adm-th">Added by</th><th className="adm-th" style={{ width: 80 }}></th></tr></thead>
          <tbody>
            {phrases.map(p => (
              <tr key={p.id}>
                <td className="adm-td primary" style={{ fontFamily: 'var(--mono)' }}>{p.phrase}</td>
                <td className="adm-td">{KINDS.find(k => k[0] === p.kind)?.[1] || p.kind}</td>
                <td className="adm-td" style={{ color: 'var(--tx-muted)' }}>{p.createdBy || 'seed'}</td>
                <td className="adm-td"><button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => removePhrase(p)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {settings && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Cut list settings</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {SETTINGS.map(([k, label, type, options]) => (
              <label key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 10, alignItems: 'center', fontSize: 12 }}>
                <span>{label}</span>
                {type === 'select' ? (
                  <select className="vc-input" data-testid={`scene-scrub-setting-${k}`} value={k in edits ? edits[k] : (settings[k] ?? options[0])} onChange={e => setEdits(d => ({ ...d, [k]: e.target.value }))}>
                    {options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : type === 'boolean' ? (
                  <select className="vc-input" data-testid={`scene-scrub-setting-${k}`} value={String(k in edits ? edits[k] : (settings[k] === true || String(settings[k]) === 'true'))} onChange={e => setEdits(d => ({ ...d, [k]: e.target.value === 'true' }))}>
                    <option value="false">off</option><option value="true">on</option>
                  </select>
                ) : (
                  <input className="vc-input" data-testid={`scene-scrub-setting-${k}`} type={type} step={type === 'number' ? 'any' : undefined} value={k in edits ? edits[k] : (settings[k] ?? '')} onChange={e => setEdits(d => ({ ...d, [k]: e.target.value }))} />
                )}
              </label>
            ))}
            {/* D-183 F-2 (CSW Order 4): the knobs tuned per kind of script, overridable per script format; empty = the global value above */}
            <div data-testid="scene-scrub-format-defaults" style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Per-format defaults</div>
              <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 6 }}>A value here wins over the global setting for cards of that script format; leave it on "global" to use the setting above. Each stored scrub records which value applied.</div>
              <table className="adm-table"><thead><tr><th className="adm-th">Format</th>{FORMAT_KNOBS.map(([k, l]) => <th key={k} className="adm-th">{l}</th>)}</tr></thead>
                <tbody>{FORMATS.map(f => (
                  <tr key={f}><td className="adm-td primary">{f}</td>
                    {FORMAT_KNOBS.map(([knob, , options]) => { const key = `format.${f}.${knob}`; const cur = key in edits ? edits[key] : (settings[key] ?? ''); const global = settings[knob];
                      return <td key={knob} className="adm-td">{options
                        ? <select className="vc-input" data-testid={`scene-scrub-format-${f}-${knob}`} value={String(cur)} onChange={e => setEdits(d => ({ ...d, [key]: e.target.value }))}><option value="">global ({String(global)})</option>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>
                        : <input className="vc-input" data-testid={`scene-scrub-format-${f}-${knob}`} type="number" step="any" min="0" max="5" placeholder={`global (${global})`} value={cur} onChange={e => setEdits(d => ({ ...d, [key]: e.target.value }))} style={{ width: 120 }} />}</td>; })}
                  </tr>))}</tbody></table>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="btn-primary" data-testid="scene-scrub-settings-save" disabled={!Object.keys(edits).length} onClick={saveSettings}>Save settings</button>
              <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>Watcher: {String(settings.watcher_enabled) === 'false' ? 'disabled' : 'enabled'} · per-client path prefixes are edited via the API (path_prefix_by_client)</span>
            </div>
          </div>
        </div>
      )}
      {msg && <div style={{ fontSize: 12, color: 'var(--s-approved-t, #6fcf97)' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12, color: 'var(--s-incomplete-t, #d45555)' }}>{err}</div>}
    </div>
  );
}
